import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ASSETS, CHIPS, TAGLINE } from '../lib/products';

const CHIP_SPOTS = [
  { x: -260, y: -120, r: -25, w: 150 }, { x: 240, y: -140, r: 30, w: 130 }, { x: -320, y: 90, r: 40, w: 110 },
  { x: 300, y: 80, r: -30, w: 140 }, { x: -60, y: -220, r: 12, w: 90 }, { x: 120, y: 170, r: -50, w: 80 },
];

export default function Preloader({ onDone }) {
  const [p, setP] = useState(0);
  const [phase, setPhase] = useState('fill');

  useEffect(() => {
    window.__lenis?.stop();
    document.body.style.overflow = 'hidden';
    let v = 0;
    const id = setInterval(() => {
      v = Math.min(100, v + 3 + Math.random() * 2);
      setP(Math.round(v));
      if (v >= 100) {
        clearInterval(id);
        setPhase('pop');
        setTimeout(() => setPhase('out'), 450);
        setTimeout(() => { document.body.style.overflow = ''; window.__lenis?.start(); onDone(); }, 1150);
      }
    }, 40);
    return () => { clearInterval(id); document.body.style.overflow = ''; };
  }, [onDone]);

  const popping = phase !== 'fill';
  const shown = Math.ceil((p / 100) * CHIP_SPOTS.length);

  return (
    <motion.div id="preloader" data-testid="preloader"
      className="fixed inset-0 z-[2400] bg-ink text-cream flex flex-col items-center justify-center overflow-hidden"
      initial={{ clipPath: 'inset(0 0 0% 0)' }}
      animate={{ clipPath: phase === 'out' ? 'inset(0 0 100% 0)' : 'inset(0 0 0% 0)' }}
      transition={{ duration: 0.7, ease: [0.83, 0, 0.17, 1] }}>
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(225,29,36,0.35),transparent_60%)]" />
      <div className="relative w-full max-w-3xl h-[60vh] flex items-center justify-center">
        {CHIP_SPOTS.map((c, i) => (
          <motion.img key={i} src={CHIPS[i]} alt="" data-testid={`preloader-chip-${i}`}
            className="absolute gpu" style={{ width: c.w }}
            initial={{ opacity: 0, scale: 0, x: 0, y: 0, rotate: 0 }}
            animate={popping
              ? { opacity: 0, scale: 1.6, x: c.x * 2.2, y: c.y * 2.2, rotate: c.r * 3 }
              : i < shown ? { opacity: 1, scale: [0, 1.25, 1], x: c.x, y: [c.y, c.y - 14, c.y], rotate: c.r } : {}}
            transition={popping ? { duration: 0.45, ease: 'easeIn' } : { scale: { duration: 0.45, ease: [0.34, 1.56, 0.64, 1] }, y: { duration: 2.4, repeat: Infinity, ease: 'easeInOut', delay: 0.45 }, default: { duration: 0.45 } }} />
        ))}
        <motion.img src={ASSETS.logo} alt="PROKRISP" data-testid="preloader-logo" className="relative z-10 h-14 sm:h-24 object-contain gpu"
          animate={popping ? { scale: 1.3, opacity: 0 } : { scale: [0.94, 1.02, 0.94], rotate: [-2, 2, -2] }}
          transition={popping ? { duration: 0.35 } : { duration: 1.6, repeat: Infinity, ease: 'easeInOut' }} />
      </div>
      <div className="relative -mt-6 text-center" data-testid="preloader-status">
        <p className="font-display text-kyellow text-4xl sm:text-6xl leading-none tracking-wide">{popping ? 'GO BEYOND' : `CRUNCHING... ${p}%`}</p>
        <div className="mt-4 mx-auto w-[60vw] max-w-sm h-2.5 rounded-full bg-cream/10 overflow-hidden">
          <div className="h-full bg-kred" style={{ width: `${p}%`, transition: 'width .1s linear' }} />
        </div>
        <p className="mt-3 font-cond text-[10px] tracking-[0.6em] text-cream/50">{TAGLINE}</p>
      </div>
    </motion.div>
  );
}
